import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Button, Steps, Typography, Select, Badge, Collapse, List, Checkbox, Alert, Tag, Spin, App, Modal, Input } from 'antd';
import { Link } from 'react-router-dom';
import StyledTable from '../Components/Tables/StyledTable';
import { primaryShades, lightBaseTokens, greyScaleTokens, variantTokens, statusTokens } from '../../theme/theme';
import { InfoCircleOutlined } from '@ant-design/icons';
import CustomTooltip from '../Components/Modals/CustomTooltip';
import HoverImageZoom from '../Components/Images/HoverImageZoom';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../data/base';
// import SkuUpgradeModal from './SkuUpgradeModal'; // Sostituito con implementazione inline
import { addProductToCatalog } from '../../services/api/cataloguesApi';
import { fetchCatalogs, fetchAvailableLicenses } from '../../services/api/cataloguesApi';
import catalogEvents, { CATALOG_EVENTS } from '../../utils/catalogEvents';
import SubscriptionOptionRenderer from './SubscriptionOptionRenderer';
import { sendUpgradeRequestEmail, getUserProfileForEmail } from '../../services/emailService';
import { fetchProductVariants } from '../../services/api/ARSCatalogVariantsApi';
import { secureGetItem } from '../../data/utils';

const { Step } = Steps;
const { Title, Text } = Typography;

/**
 * A stepper component for adding products to a catalog
 * 
 * @param {Object} props - Component props
 * @param {Object} props.selectedProduct - The selected product (must include list_variants)
 * @param {Array} props.variants - [DEPRECATED] Array of variants (now loaded internally)
 * @param {Object} props.selectedVariants - Object containing selected variants by product ID
 * @param {Function} props.handleVariantSelection - Function to handle variant selection
 * @param {Array} props.userCatalogs - Array of available catalogs
 * @param {string} props.selectedCatalog - ID of the selected catalog
 * @param {Function} props.setSelectedCatalog - Function to set the selected catalog
 * @param {Function} props.onCancel - Function to handle cancel
 * @param {Function} props.onFinish - Function to handle finish
 * @param {Function} props.currentStep - Current step
 * @param {Function} props.setCurrentStep - Function to set the current step
 */
const AddToCatalogStepper = ({
    selectedProduct,
    variants, // Mantengo la prop per compatibilità, ma caricheremo le varianti internamente
    selectedVariants,
    handleVariantSelection,
    selectedCatalog,
    setSelectedCatalog,
    onCancel,
    onFinish,
    currentStep,
    setCurrentStep
}) => {
    // Hook per accedere al message API
    const { message } = App.useApp();

    // 🎯 NUOVO: Stato per le varianti caricate internamente
    const [internalVariants, setInternalVariants] = useState({});
    const [loadingVariants, setLoadingVariants] = useState(false);

    // Stato per i cataloghi
    const [catalogsData, setCatalogsData] = useState([]);
    const [loadingCatalogs, setLoadingCatalogs] = useState(false);
    const [userCatalogues, setUserCatalogues] = useState([]);
    const [catalogNames, setCatalogNames] = useState({});

    // 🎯 REF per evitare ricaricamenti multipli
    const userCataloguesRef = useRef(null);
    const catalogNamesRef = useRef(null);

    // Stato per le licenze
    const [loadingLicenses, setLoadingLicenses] = useState(false);
    const [selectedLicense, setSelectedLicense] = useState(null);
    const [subscriptionSkuData, setSubscriptionSkuData] = useState({});

    // Stato per i cataloghi delle varianti
    const [variantCatalogues, setVariantCatalogues] = useState({});
    const [loadingVariantCatalogues, setLoadingVariantCatalogues] = useState(false);

    // Stato per upgrade modal
    const [upgradeModal, setUpgradeModal] = useState({ visible: false, subscription: null });
    const [upgradeMessage, setUpgradeMessage] = useState('');
    const [sendingUpgradeRequest, setSendingUpgradeRequest] = useState(false);

    // Stato per il processo di aggiunta al catalogo
    const [addingToCatalog, setAddingToCatalog] = useState(false);
    const [addResult, setAddResult] = useState(null);

    // 🎯 NUOVO: Effect per caricare le varianti quando il prodotto selezionato cambia
    useEffect(() => {
        const loadVariants = async () => {
            if (!selectedProduct?.id || !selectedProduct?.list_variants?.length) {
                setInternalVariants({});
                return;
            }

            setLoadingVariants(true);
            try {
                // Uso un ref vuoto e una cache temporanea per la compatibilità con fetchProductVariants
                const tempCache = {};
                const tempRef = { current: 0 };

                await fetchProductVariants(
                    selectedProduct.id,
                    selectedProduct.list_variants,
                    { current: tempCache },
                    setInternalVariants,
                    tempRef
                );
            } catch (error) {
                console.error('Error loading variants in stepper:', error);
                message.error('Errore nel caricamento delle varianti');
            } finally {
                setLoadingVariants(false);
            }
        };

        loadVariants();
    }, [selectedProduct?.id, selectedProduct?.list_variants]);

    // Effect per resettare le select quando il componente viene montato o quando selectedProduct cambia
    useEffect(() => {
        // Reset delle select quando il componente viene montato o quando cambia il prodotto selezionato
        setSelectedLicense(null);
        setSelectedCatalog(null);
        setCurrentStep(0);
    }, [selectedProduct?.id]); // Dipende dall'ID del prodotto selezionato

    // Funzione per trovare i cataloghi delle varianti
    const findVariantCatalogues = async (productId, variantIds) => {
        if (!productId || !variantIds || variantIds.length === 0) return {};

        try {
            setLoadingVariantCatalogues(true);

            // Struttura per memorizzare il risultato: { variantId: [catalogInfo, ...] }
            const variantCataloguesResult = {};

            // Inizializza l'oggetto risultato con array vuoti per ogni variante
            variantIds.forEach(variantId => {
                variantCataloguesResult[variantId] = [];
            });

            // 1. Trova tutti i CataloguesProducts che sono stati clonati dal prodotto selezionato
            const cataloguesProductsRef = collection(db, "CataloguesProducts");
            const cataloguesProductsQuery = query(
                cataloguesProductsRef,
                where("mainProduct", "==", productId)
            );
            const cataloguesProductsSnapshot = await getDocs(cataloguesProductsQuery);

            // Se non ci sono prodotti nei cataloghi, restituisci subito il risultato vuoto
            if (cataloguesProductsSnapshot.empty) {
                setLoadingVariantCatalogues(false);
                return variantCataloguesResult;
            }

            // Crea una mappa di catalogueProductId -> catalogRef per uso successivo
            // Ma filtra solo per i cataloghi dell'utente
            const catalogueProductMap = {};
            cataloguesProductsSnapshot.forEach(doc => {
                const catalogRef = doc.data().catalogRef;
                // 🎯 FILTRO: Include solo i cataloghi dell'utente
                if (userCatalogues.includes(catalogRef)) {
                    catalogueProductMap[doc.id] = catalogRef;
                }
            });

            // Se non ci sono cataloghi dell'utente con questo prodotto, restituisci vuoto
            if (Object.keys(catalogueProductMap).length === 0) {
                setLoadingVariantCatalogues(false);
                return variantCataloguesResult;
            }

            // 2. Per evitare l'errore "Too many disjunctions", facciamo query separate per ogni variante
            // Questo è più lento ma evita il limite di Firestore
            const cataloguesVariantsRef = collection(db, "CataloguesVariants");

            // Processa ogni variante singolarmente
            for (const variantId of variantIds) {
                try {
                    // Query per trovare tutte le CataloguesVariants clonate da questa variante specifica
                    const variantQuery = query(
                        cataloguesVariantsRef,
                        where("mainVariantRef", "==", variantId)
                    );

                    const variantSnapshot = await getDocs(variantQuery);

                    // Per ogni CataloguesVariant trovata, controlla se appartiene a uno dei nostri cataloghi
                    variantSnapshot.forEach(doc => {
                        const data = doc.data();
                        const catalogProductRef = data.catalogProductRef;

                        // Verifica se questa variante appartiene a uno dei prodotti del nostro catalogo
                        // 🎯 Il filtro per userCatalogues è già applicato in catalogueProductMap
                        if (catalogProductRef && catalogueProductMap[catalogProductRef]) {
                            const catalogId = catalogueProductMap[catalogProductRef];

                            // Aggiungi il catalogo all'array della variante se non è già presente
                            if (!variantCataloguesResult[variantId].some(cat => cat.id === catalogId)) {
                                variantCataloguesResult[variantId].push({
                                    id: catalogId,
                                    catalogueVariantId: doc.id
                                });
                            }
                        }
                    });
                } catch (variantError) {
                    console.error(`Error processing variant ${variantId}:`, variantError);
                    // Continua con le altre varianti anche se una fallisce
                }
            }

            setLoadingVariantCatalogues(false);
            return variantCataloguesResult;
        } catch (error) {
            console.error("Error finding variant catalogues:", error);
            setLoadingVariantCatalogues(false);
            return {};
        }
    };

    // Seleziona le licenze disponibili dal Redux store
    const availableLicenses = useSelector(state => state.licenses.availableLicenses);

    // Funzione per calcolare SKU utilizzati per una subscription
    const calculateSubscriptionSkuUsed = async (catalogRefList) => {
        try {
            if (!catalogRefList || catalogRefList.length === 0) return 0;

            let totalSkuCount = 0;

            // Query CataloguesProducts per ogni catalogo
            for (const catalogRef of catalogRefList) {
                const productsQuery = query(
                    collection(db, "CataloguesProducts"),
                    where("catalogRef", "==", catalogRef)
                );

                const productsSnapshot = await getDocs(productsQuery);

                // Somma tutte le lunghezze di list_variants
                productsSnapshot.docs.forEach(productDoc => {
                    const productData = productDoc.data();
                    const variants = productData.list_variants || [];
                    totalSkuCount += variants.length;
                });
            }

            return totalSkuCount;
        } catch (error) {
            console.error('Error calculating subscription SKU used:', error);
            return 0;
        }
    };

    // Funzione per ottenere informazioni sulla subscription
    const getSubscriptionInfo = (subscription, selectedVariantsCount) => {
        const catalogRefList = subscription.catalogRefList || [];
        const skuLimit = subscription.skuLimit || 0;

        // Se skuLimit è 0, significa unlimited
        if (skuLimit === 0) {
            // Controlla se ci sono cataloghi disponibili nella licenza
            const hasAvailableCatalogs = catalogRefList.some(catalogId => {
                return catalogsData.some(cat => cat.id === catalogId);
            });

            return {
                hasAvailableCatalogs: hasAvailableCatalogs,
                availableSkus: "No Limits",
                needsUpgrade: false
            };
        }

        const skuData = subscriptionSkuData[subscription.id];
        if (!skuData) {
            return {
                hasAvailableCatalogs: false,
                availableSkus: 0,
                needsUpgrade: false,
                isLoading: true
            };
        }

        const usedSkus = skuData.usedSkus || 0;
        const availableSkus = skuLimit - usedSkus;

        // Controlla se ci sono cataloghi disponibili nella licenza
        const hasAvailableCatalogs = catalogRefList.some(catalogId => {
            return catalogsData.some(cat => cat.id === catalogId);
        });

        // Controlla se l'aggiunta delle varianti selezionate supererebbe il limite
        const needsUpgrade = selectedVariantsCount > availableSkus;

        return {
            hasAvailableCatalogs: hasAvailableCatalogs,
            availableSkus: availableSkus,
            needsUpgrade: needsUpgrade
        };
    };

    // Funzione per caricare le licenze e calcolare gli SKU
    const loadLicenses = async () => {
        setLoadingLicenses(true);
        try {
            const licenses = await fetchAvailableLicenses();

            // Calcola gli SKU utilizzati per ogni subscription
            const skuDataPromises = licenses.map(async (license) => {
                const catalogRefList = license.catalogRefList || [];
                const usedSkus = await calculateSubscriptionSkuUsed(catalogRefList);
                return {
                    id: license.id,
                    usedSkus,
                    catalogRefList
                };
            });

            const skuDataResults = await Promise.all(skuDataPromises);
            const skuDataMap = {};
            skuDataResults.forEach(data => {
                skuDataMap[data.id] = data;
            });

            setSubscriptionSkuData(skuDataMap);
        } catch (error) {
            console.error('Error fetching licenses:', error);
            message.error('Failed to load available licenses.');
        } finally {
            setLoadingLicenses(false);
        }
    };

    // Carica le licenze all'avvio
    useEffect(() => {
        loadLicenses();
    }, []);

    // 🎯 NUOVO: Carica i cataloghi quando viene selezionata una subscription
    useEffect(() => {
        const loadCatalogsForSubscription = async () => {
            if (!selectedLicense || !availableLicenses.length) {
                setCatalogsData([]);
                return;
            }

            setLoadingCatalogs(true);
            try {
                // Trova la subscription selezionata
                const selectedSubscription = availableLicenses.find(license => license.id === selectedLicense);
                if (!selectedSubscription || !selectedSubscription.catalogRefList) {
                    setCatalogsData([]);
                    setLoadingCatalogs(false);
                    return;
                }

                // Carica i dettagli dei cataloghi dalla subscription
                const catalogPromises = selectedSubscription.catalogRefList.map(async (catalogId) => {
                    try {
                        const docRef = doc(db, 'Catalogues', catalogId);
                        const docSnap = await getDoc(docRef);

                        if (docSnap.exists()) {
                            const data = docSnap.data();
                            return {
                                id: docSnap.id,
                                nameCatalog: data.nameCatalog || data.name || `Catalog ${docSnap.id.substring(0, 8)}`,
                                name: data.name || data.nameCatalog || `Catalog ${docSnap.id.substring(0, 8)}`
                            };
                        }
                        return null;
                    } catch (error) {
                        console.error(`Error fetching catalog ${catalogId}:`, error);
                        return null;
                    }
                });

                const results = await Promise.all(catalogPromises);
                const validCatalogs = results.filter(c => c !== null);
                setCatalogsData(validCatalogs);
            } catch (error) {
                console.error('Error loading catalogs for subscription:', error);
                setCatalogsData([]);
            } finally {
                setLoadingCatalogs(false);
            }
        };

        loadCatalogsForSubscription();
    }, [selectedLicense, availableLicenses]); // Si ricarica quando cambia la licenza selezionata

    // 🎯 CARICA userCatalogues UNA VOLTA SOLA (usando ref per evitare re-mount)
    useEffect(() => {
        // Se già caricato, non ricaricare
        if (userCataloguesRef.current !== null) {
            setUserCatalogues(userCataloguesRef.current);
            if (catalogNamesRef.current !== null) {
                setCatalogNames(catalogNamesRef.current);
            }
            return;
        }

        const userCatalogsList = secureGetItem('p_list_catalogues') || [];
        userCataloguesRef.current = userCatalogsList;
        setUserCatalogues(userCatalogsList);

        // Carica i nomi dei cataloghi
        if (userCatalogsList.length > 0) {
            const catalogNamesMap = {};
            const catalogPromises = userCatalogsList.map(async (catalogId) => {
                try {
                    const docRef = doc(db, 'Catalogues', catalogId);
                    const docSnap = await getDoc(docRef);
                    if (docSnap.exists()) {
                        catalogNamesMap[catalogId] = docSnap.data().nameCatalog || `Catalog ${catalogId.substring(0, 5)}...`;
                    } else {
                        catalogNamesMap[catalogId] = `Catalog ${catalogId.substring(0, 5)}...`;
                    }
                } catch (error) {
                    catalogNamesMap[catalogId] = `Catalog ${catalogId.substring(0, 5)}...`;
                }
            });

            Promise.all(catalogPromises).then(() => {
                catalogNamesRef.current = catalogNamesMap;
                setCatalogNames(catalogNamesMap);
            });
        }
    }, []); // SOLO UNA VOLTA all'avvio

    // 🎯 CHIAMA findVariantCatalogues quando tutto è pronto
    useEffect(() => {
        if (selectedProduct?.id && internalVariants[selectedProduct.id] && userCatalogues.length > 0) {
            const variantIds = internalVariants[selectedProduct.id]
                .filter(variant => variant.status === 'Published')
                .map(variant => variant.id);

            findVariantCatalogues(selectedProduct.id, variantIds).then(result => {
                setVariantCatalogues(result);
            });
        }
    }, [selectedProduct?.id, internalVariants, userCatalogues.length]);  // Dipende da userCatalogues

    // Listener per gli eventi dei cataloghi
    useEffect(() => {
        const handleCatalogPublished = async () => {
            // Pulisci la cache dei variantCatalogues per forzare il ricaricamento
            setVariantCatalogues({});

            // Aggiungi un piccolo delay per dare tempo al database di sincronizzarsi
            await new Promise(resolve => setTimeout(resolve, 1000));

            // 🎯 NUOVO: Ricarica anche i cataloghi della subscription selezionata
            if (selectedLicense && availableLicenses.length > 0) {
                const selectedSubscription = availableLicenses.find(license => license.id === selectedLicense);
                if (selectedSubscription && selectedSubscription.catalogRefList) {
                    setLoadingCatalogs(true);
                    try {
                        const catalogPromises = selectedSubscription.catalogRefList.map(async (catalogId) => {
                            try {
                                const docRef = doc(db, 'Catalogues', catalogId);
                                const docSnap = await getDoc(docRef);

                                if (docSnap.exists()) {
                                    const data = docSnap.data();
                                    return {
                                        id: docSnap.id,
                                        nameCatalog: data.nameCatalog || data.name || `Catalog ${docSnap.id.substring(0, 8)}`,
                                        name: data.name || data.nameCatalog || `Catalog ${docSnap.id.substring(0, 8)}`
                                    };
                                }
                                return null;
                            } catch (error) {
                                console.error(`Error fetching catalog ${catalogId}:`, error);
                                return null;
                            }
                        });

                        const results = await Promise.all(catalogPromises);
                        const validCatalogs = results.filter(c => c !== null);
                        setCatalogsData(validCatalogs);
                    } catch (error) {
                        console.error('Error reloading catalogs after catalog published:', error);
                    } finally {
                        setLoadingCatalogs(false);
                    }
                }
            }

            // Ricarica solo le varianti dei cataloghi quando viene pubblicato un nuovo catalogo
            if (selectedProduct?.id && internalVariants[selectedProduct.id] && userCatalogues.length > 0) {
                const variantIds = internalVariants[selectedProduct.id]
                    .filter(variant => variant.status === 'Published')
                    .map(variant => variant.id);

                findVariantCatalogues(selectedProduct.id, variantIds).then(result => {
                    setVariantCatalogues(result);
                });
            }
        };

        // Registra il listener
        catalogEvents.on(CATALOG_EVENTS.CATALOG_PUBLISHED, handleCatalogPublished);
        catalogEvents.on(CATALOG_EVENTS.CATALOGS_REFRESHED, handleCatalogPublished);

        // Cleanup function per rimuovere il listener quando il componente viene smontato
        return () => {
            catalogEvents.off(CATALOG_EVENTS.CATALOG_PUBLISHED, handleCatalogPublished);
            catalogEvents.off(CATALOG_EVENTS.CATALOGS_REFRESHED, handleCatalogPublished);
        };
    }, [selectedLicense, availableLicenses]); // Aggiunte dipendenze per il ricaricamento dei cataloghi

    // Check if any variants are selected for the current product
    const hasSelectedVariants = selectedProduct &&
        selectedVariants[selectedProduct.id] &&
        selectedVariants[selectedProduct.id].length > 0;

    // Check if both license and catalog are selected
    const hasLicenseSelected = !!selectedLicense;
    const hasCatalogSelected = !!selectedCatalog;

    // Controlla se la subscription selezionata ha superato il limite
    const selectedSubscriptionExceedsLimit = React.useMemo(() => {
        if (!selectedLicense || !availableLicenses.length) return false;

        const subscription = availableLicenses.find(license => license.id === selectedLicense);
        if (!subscription) return false;

        const selectedVariantsCount = selectedProduct && selectedVariants[selectedProduct.id]
            ? selectedVariants[selectedProduct.id].length
            : 0;
        const subscriptionInfo = getSubscriptionInfo(subscription, selectedVariantsCount);

        return subscriptionInfo.needsUpgrade;
    }, [selectedLicense, availableLicenses, selectedProduct, selectedVariants, subscriptionSkuData, catalogsData]);



    // Function to sort licenses by subscription type
    const sortLicensesByType = (licenses) => {
        return [...licenses].sort((a, b) => {
            const getTypeCategory = (subscriptionType) => {
                if (!subscriptionType) return 999;
                const type = subscriptionType.toLowerCase();
                if (type.includes('additional')) return 2;
                if (type.includes('branded')) return 0;
                if (type.includes('smart')) return 1;
                return 999;
            };

            const categoryA = getTypeCategory(a.subscriptionType);
            const categoryB = getTypeCategory(b.subscriptionType);

            if (categoryA === categoryB) {
                return (a.name || '').localeCompare(b.name || '');
            }

            return categoryA - categoryB;
        });
    };

    // Filter and sort licenses
    const getFilteredLicenses = () => {
        const filteredLicenses = availableLicenses.filter(license => {
            const subscriptionType = license.subscriptionType?.toLowerCase() || '';
            return !subscriptionType.includes('gateway');
        });
        return sortLicensesByType(filteredLicenses);
    };

    // Function to handle selecting all variants
    const handleSelectAllVariants = (checked) => {
        if (!selectedProduct || !internalVariants[selectedProduct.id]) return;

        const productId = selectedProduct.id;
        const publishedVariants = internalVariants[productId].filter(variant => variant.status === 'Published');

        if (checked) {
            // Select all variants - we need to select them one by one
            publishedVariants.forEach(variant => {
                if (!selectedVariants[productId]?.includes(variant.id)) {
                    handleVariantSelection(productId, variant.id, true);
                }
            });
        } else {
            // Deselect all variants - we need to deselect them one by one
            const currentlySelected = selectedVariants[productId] || [];
            currentlySelected.forEach(variantId => {
                handleVariantSelection(productId, variantId, false);
            });
        }
    };

    // Function to check if all variants are selected
    const areAllVariantsSelected = () => {
        if (!selectedProduct || !internalVariants[selectedProduct.id]) return false;

        const publishedVariants = internalVariants[selectedProduct.id].filter(v => v.status === 'Published');
        if (publishedVariants.length === 0) return false;

        const selectedVariantsForProduct = selectedVariants[selectedProduct.id] || [];

        // Check if all published variants are in the selected variants array
        return publishedVariants.every(variant => selectedVariantsForProduct.includes(variant.id));
    };

    // Definizione diretta delle colonne per la tabella delle varianti
    const getStepperVariantColumns = () => {
        // Colonna di selezione
        const selectColumn = {
            title: (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                    <Checkbox
                        checked={areAllVariantsSelected()}
                        onChange={(e) => handleSelectAllVariants(e.target.checked)}
                    />
                </div>
            ),
            dataIndex: 'select',
            key: 'select',
            fixed: 'left',
            width: '3%',
            render: (_, variant) => (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                    <Checkbox
                        checked={selectedVariants[variant.productId]?.includes(variant.id) || false}
                        onChange={(e) => handleVariantSelection(variant.productId, variant.id, e.target.checked)}
                    />
                </div>
            ),
        };

        // Colonna immagine
        const imageColumn = {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span>Image</span>
                    <CustomTooltip title="Image of the variant">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'poster',
            key: 'poster',
            width: '6%',
            align: 'center',
            render: (imgUrl) => (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                    <HoverImageZoom
                        src={imgUrl}
                        alt="Variant"
                        style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                    />
                </div>
            ),
        };

        // Colonna nome prodotto
        const productNameColumn = {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <span>Product Name</span>
                    <CustomTooltip title="Name of the product">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'glassesName',
            key: 'glassesName',
            width: '12%',
            align: 'left',
        };

        // Colonna SKU
        const skuColumn = {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <span>SKU</span>
                    <CustomTooltip title="SKU of the model">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'skuModel',
            key: 'skuModel',
            width: '8%',
            align: 'left',
        };

        // Colonna EAN
        const eanColumn = {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <span>EAN</span>
                    <CustomTooltip title="EAN code of the variant">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'eanCode',
            key: 'eanCode',
            width: '8%',
            align: 'left',
            render: (text) => text || '-',
        };

        // Colonna Frame Color
        const frameColorColumn = {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <span>Frame Color</span>
                    <CustomTooltip title="Color of the frame">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'frameColor',
            key: 'frameColor',
            width: '8%',
            align: 'left',
        };

        // Colonna Lenses Color
        const lensesColorColumn = {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <span>Lenses Color</span>
                    <CustomTooltip title="Color of the lenses">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'lensesColor',
            key: 'lensesColor',
            width: '8%',
            align: 'left',
            render: (text) => text === "0" || !text ? "TRANSPARENT" : text,
        };

        // Colonna "In my catalogue(s)"
        const inMyCataloguesColumn = {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <span>In my catalogue(s)</span>
                    <CustomTooltip title="Shows if the variant is already present in your own catalogues">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'id',
            key: 'inMyCatalogues',
            width: '15%',
            align: 'left',
            render: (variantId) => {
                if (loadingVariantCatalogues) {
                    return <Spin size="small" />;
                }

                const catalogsList = variantCatalogues[variantId] || [];

                if (catalogsList.length === 0) {
                    return '-';
                }

                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {catalogsList.map(catalog => (
                            <Tag
                                key={catalog.id}
                                style={{
                                    margin: 0,
                                    backgroundColor: greyScaleTokens.grey200,
                                    color: '#000',
                                    border: 'none',
                                    borderRadius: '2px'
                                }}
                            >
                                {catalogNames[catalog.id] ? catalogNames[catalog.id] : `Catalogo ${catalog.id.substring(0, 5)}...`}
                            </Tag>
                        ))}
                    </div>
                );
            },
        };

        // Colonna Size
        const sizeColumn = {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <span>Size</span>
                    <CustomTooltip title="Size of the variant">
                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'size',
            key: 'size',
            width: '5%',
            align: 'left',
            render: (size) => size?.size || '-',
        };

        // Restituisce tutte le colonne nell'ordine desiderato
        return [
            selectColumn,
            imageColumn,
            productNameColumn,
            skuColumn,
            eanColumn,
            frameColorColumn,
            lensesColorColumn,
            inMyCataloguesColumn,
            sizeColumn,
        ];
    };

    // Steps content
    const steps = [
        {
            title: 'Select Variants',
            content: (
                <div style={{ padding: '20px 0' }}>
                    {selectedProduct && selectedProduct.catalogRef && userCatalogues.includes(selectedProduct.catalogRef) && (
                        <Alert
                            message={
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <span>This product is already in your catalogue: </span>
                                    <span style={{ marginLeft: '5px', color: lightBaseTokens.colorSuccess, fontWeight: 'bold' }}>
                                        {catalogNames[selectedProduct.catalogRef] || selectedProduct.catalogRef}
                                    </span>
                                    <CustomTooltip title="This product is already in your catalogue">
                                        <InfoCircleOutlined style={{ marginLeft: '8px', cursor: 'pointer' }} />
                                    </CustomTooltip>
                                </div>
                            }
                            type="info"
                            showIcon
                            style={{ marginBottom: '20px' }}
                        />
                    )}
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                        <Text>
                            Please select the variants you want to add to the catalog. You can select multiple variants at once.
                        </Text>
                    </div>
                    <div style={{ marginTop: '20px' }}>
                        <Title level={5}>Available Variants</Title>
                        <div style={{ maxHeight: '600px', overflow: 'auto' }}>
                            <StyledTable
                                columns={getStepperVariantColumns()}
                                dataSource={(internalVariants[selectedProduct?.id] || [])
                                    .filter(variant => variant.status === 'Published')
                                    .map(variant => ({
                                        ...variant,
                                        productId: selectedProduct.id
                                    }))}
                                rowKey="id"
                                pagination={null}
                                size="middle"
                                scroll={{ x: 980 }}
                                containerStyle={{ boxShadow: 'none', border: '1px solid #e0e0e0', borderRadius: '8px' }}
                                loading={loadingVariants}
                                locale={{
                                    emptyText: loadingVariants ?
                                        <Spin size="small" /> :
                                        'Nessuna variante pubblicata disponibile'
                                }}
                            />
                        </div>
                    </div>
                </div>
            ),
        },
        {
            title: 'Select Catalogue',
            content: (
                <div style={{ padding: '20px 0' }}>
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                        <Text>
                            Please select an active subscription plan and
                            then the catalogue where you want to add the selected variants.
                        </Text>
                    </div>

                    <div style={{ maxWidth: '31.25rem', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {/* License Selection */}
                        <div>
                            <Title level={5}>Select Subscription</Title>
                            <Select
                                placeholder="Select a subscription"
                                style={{ width: '100%' }}
                                value={selectedLicense}
                                onChange={(value) => {
                                    setSelectedLicense(value);
                                    // Reset catalog selection when license changes
                                    setSelectedCatalog(null);
                                }}
                                loading={loadingLicenses}
                                optionLabelProp="label"
                                showSearch
                                filterOption={(input, option) =>
                                    option?.label?.toLowerCase().includes(input.toLowerCase())
                                }
                            >
                                {getFilteredLicenses().map(subscription => {
                                    const selectedVariantsCount = selectedProduct && selectedVariants[selectedProduct.id]
                                        ? selectedVariants[selectedProduct.id].length
                                        : 0;

                                    const subscriptionInfo = getSubscriptionInfo(subscription, selectedVariantsCount);

                                    return (
                                        <Select.Option
                                            key={subscription.id}
                                            value={subscription.id}
                                            label={subscription.name}
                                            disabled={subscriptionInfo.needsUpgrade}
                                        >
                                            <SubscriptionOptionRenderer
                                                subscription={subscription}
                                                subscriptionInfo={subscriptionInfo}
                                                selectedVariantsCount={selectedVariantsCount}
                                                onUpgradeClick={() => setUpgradeModal({ visible: true, subscription: subscription })}
                                            />
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        </div>

                        {/* Messaggio di errore per subscription che supera il limite */}
                        {selectedSubscriptionExceedsLimit && (
                            <div style={{
                                marginBottom: '1rem',
                                padding: '1rem',
                                backgroundColor: '#fff2f0',
                                borderRadius: '0.5rem',
                                border: '1px solid #ffccc7'
                            }}>
                                <Text style={{ fontSize: '0.875rem', color: '#ff4d4f' }}>
                                    ⚠️ This subscription has exceeded its SKU limit. Please request an upgrade to continue.
                                </Text>
                            </div>
                        )}

                        {/* Catalog Selection */}
                        <div>
                            <Title level={5}>Available Catalogues</Title>
                            <Select
                                placeholder="Select a catalogue"
                                style={{ width: '100%' }}
                                value={selectedCatalog}
                                disabled={!selectedLicense || selectedSubscriptionExceedsLimit}
                                onChange={setSelectedCatalog}
                                loading={loadingCatalogs}
                                options={catalogsData
                                    // Filtro per licenza selezionata (controlla list_catalogues_refs)
                                    .filter(catalog => {
                                        if (!selectedLicense) return false;

                                        // Trova la licenza selezionata nell'array availableLicenses
                                        const selectedLicenseData = availableLicenses.find(license => license.id === selectedLicense);
                                        if (!selectedLicenseData) return false;

                                        // Controlla se il catalogo è presente in list_catalogues_refs della licenza
                                        const catalogRefList = selectedLicenseData.catalogRefList || [];
                                        return catalogRefList.includes(catalog.id);
                                    })
                                    .map(catalog => ({
                                        value: catalog.id,
                                        label: catalog.nameCatalog || 'Unnamed Catalog'
                                    }))
                                    .sort((a, b) => (a.label || '').localeCompare(b.label || ''))}
                                optionFilterProp="label"
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                notFoundContent={
                                    !selectedLicense ?
                                        <span>Please select a subscription first</span> :
                                        selectedSubscriptionExceedsLimit ?
                                            <span>Subscription exceeds SKU limit</span> :
                                            loadingCatalogs ?
                                                <span>Loading catalogues...</span> :
                                                <span>No catalogue found</span>
                                }
                            />
                        </div>


                    </div>
                </div>
            ),
        },
        {
            title: 'Review & Confirm',
            content: (
                <div style={{ padding: '20px 0' }}>
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                        <Text>
                            Please review the variants that will be added to your catalogue. Disabled variants are already present in the selected catalogue.
                        </Text>
                    </div>

                    {selectedProduct && selectedCatalog && (
                        <div>
                            <div style={{ marginBottom: '1rem' }}>
                                <p><strong>Catalogue:</strong> {catalogsData.find(cat => cat.id === selectedCatalog)?.nameCatalog}</p>
                            </div>

                            {/* Variants Review Table */}
                            {selectedVariants[selectedProduct.id]?.length > 0 && (
                                <div style={{ marginBottom: '1rem' }}>
                                    <Title level={5}>Selected Variants Review</Title>
                                    <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                                        <StyledTable
                                            columns={[
                                                {
                                                    title: 'Image',
                                                    dataIndex: 'poster',
                                                    key: 'poster',
                                                    width: '10%',
                                                    align: 'center',
                                                    render: (imgUrl, record) => (
                                                        <div style={{
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            opacity: record.alreadyInCatalog ? 0.4 : 1
                                                        }}>
                                                            <HoverImageZoom
                                                                src={imgUrl}
                                                                alt="Variant"
                                                                style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                                                            />
                                                        </div>
                                                    ),
                                                },
                                                {
                                                    title: 'Product Name',
                                                    dataIndex: 'glassesName',
                                                    key: 'glassesName',
                                                    width: '25%',
                                                    align: 'left',
                                                    render: (text, record) => (
                                                        <span style={{
                                                            color: record.alreadyInCatalog ? greyScaleTokens.grey500 : 'inherit',
                                                            textDecoration: record.alreadyInCatalog ? 'line-through' : 'none'
                                                        }}>
                                                            {text}
                                                        </span>
                                                    ),
                                                },
                                                {
                                                    title: 'SKU',
                                                    dataIndex: 'skuModel',
                                                    key: 'skuModel',
                                                    width: '15%',
                                                    align: 'left',
                                                    render: (text, record) => (
                                                        <span style={{
                                                            color: record.alreadyInCatalog ? greyScaleTokens.grey500 : 'inherit',
                                                            textDecoration: record.alreadyInCatalog ? 'line-through' : 'none'
                                                        }}>
                                                            {text || '-'}
                                                        </span>
                                                    ),
                                                },
                                                {
                                                    title: 'Frame Colour',
                                                    dataIndex: 'frameColor',
                                                    key: 'frameColor',
                                                    width: '15%',
                                                    align: 'left',
                                                    render: (text, record) => (
                                                        <span style={{
                                                            color: record.alreadyInCatalog ? greyScaleTokens.grey500 : 'inherit',
                                                            textDecoration: record.alreadyInCatalog ? 'line-through' : 'none'
                                                        }}>
                                                            {text || '-'}
                                                        </span>
                                                    ),
                                                },
                                                {
                                                    title: 'Status',
                                                    key: 'status',
                                                    width: '20%',
                                                    align: 'center',
                                                    render: (_, record) => (
                                                        <Tag
                                                            color={record.alreadyInCatalog ? statusTokens.warning : statusTokens.success}
                                                            style={{
                                                                fontSize: '0.75rem',
                                                                backgroundColor: record.alreadyInCatalog ? statusTokens.warning : statusTokens.success,
                                                                color: variantTokens.white,
                                                                border: 'none'
                                                            }}
                                                        >
                                                            {record.alreadyInCatalog ? 'Already in Catalogue' : 'Will be Added'}
                                                        </Tag>
                                                    ),
                                                },
                                            ]}
                                            dataSource={internalVariants[selectedProduct.id]
                                                ?.filter(variant => selectedVariants[selectedProduct.id]?.includes(variant.id))
                                                .map(variant => {
                                                    // Controlla se questa variante è già presente nel catalogo selezionato
                                                    const catalogsForVariant = variantCatalogues[variant.id] || [];
                                                    const alreadyInCatalog = catalogsForVariant.some(cat => cat.id === selectedCatalog);

                                                    return {
                                                        ...variant,
                                                        alreadyInCatalog
                                                    };
                                                }) || []
                                            }
                                            rowKey="id"
                                            pagination={null}
                                            size="small"
                                            scroll={{ x: 800 }}
                                            containerStyle={{
                                                boxShadow: 'none',
                                                border: `1px solid ${greyScaleTokens.grey300}`,
                                                borderRadius: '8px'
                                            }}
                                            loading={loadingVariantCatalogues}
                                            rowClassName={(record) => record.alreadyInCatalog ? 'disabled-row' : ''}
                                        />
                                    </div>

                                    {/* Informational Message - Solo se ci sono varianti già presenti */}
                                    {(() => {
                                        const variantsData = internalVariants[selectedProduct.id]
                                            ?.filter(variant => selectedVariants[selectedProduct.id]?.includes(variant.id))
                                            .map(variant => {
                                                const catalogsForVariant = variantCatalogues[variant.id] || [];
                                                const alreadyInCatalog = catalogsForVariant.some(cat => cat.id === selectedCatalog);
                                                return { ...variant, alreadyInCatalog };
                                            }) || [];

                                        const hasVariantsAlreadyInCatalog = variantsData.some(variant => variant.alreadyInCatalog);

                                        return hasVariantsAlreadyInCatalog ? (
                                            <Alert
                                                message="Variant Status Information"
                                                description="Variants marked as 'Already in Catalogue' are already present in the selected catalogue and will not be processed. Only variants marked as 'Will be Added' will be added to your catalogue."
                                                type="info"
                                                showIcon
                                                style={{
                                                    marginTop: '1rem',
                                                    marginBottom: '1rem',
                                                    backgroundColor: primaryShades.primary100,
                                                    border: `1px solid ${primaryShades.primary200}`,
                                                    borderRadius: '8px'
                                                }}
                                            />
                                        ) : null;
                                    })()}
                                </div>
                            )}

                            <div style={{
                                marginTop: '1rem',
                                padding: '1rem',
                                backgroundColor: greyScaleTokens.grey100,
                                borderRadius: '8px',
                                border: `1px solid ${greyScaleTokens.grey200}`
                            }}>
                                <Text style={{ color: primaryShades.primary600, fontWeight: '500' }}>
                                    Proceed to add the available variants to your catalogue?
                                </Text>
                            </div>
                        </div>
                    )}
                </div>
            ),
        },
    ];

    // Function to handle next step
    const handleNext = () => {
        setCurrentStep(currentStep + 1);
    };

    // Function to handle previous step
    const handlePrev = () => {
        setCurrentStep(currentStep - 1);
    };

    // Determine if the Next button should be disabled
    const isNextDisabled = () => {
        if (currentStep === 0) {
            return !hasSelectedVariants;
        } else if (currentStep === 1) {
            return !hasLicenseSelected || !hasCatalogSelected || selectedSubscriptionExceedsLimit;
        }
        return false;
    };

    return (
        <div>
            <style>
                {`
                    .disabled-row {
                        background-color: ${greyScaleTokens.grey100} !important;
                        opacity: 0.6;
                    }
                    .disabled-row:hover {
                        background-color: ${greyScaleTokens.grey100} !important;
                    }
                `}
            </style>
            <Steps current={currentStep} style={{ marginBottom: '24px' }}>
                {steps.map(item => (
                    <Step key={item.title} title={item.title} />
                ))}
            </Steps>

            <div className="steps-content">{steps[currentStep].content}</div>

            <div className="steps-action" style={{ marginTop: '24px', display: 'flex', justifyContent: 'space-between' }}>
                {currentStep > 0 && (
                    <Button onClick={handlePrev}>
                        Previous
                    </Button>
                )}

                {currentStep === 0 && (
                    <Button onClick={onCancel}>
                        Cancel
                    </Button>
                )}

                {currentStep < steps.length - 1 && (
                    <Button type="primary" onClick={handleNext} disabled={isNextDisabled()}>
                        Next
                    </Button>
                )}

                {currentStep === steps.length - 1 && (
                    <Button
                        type="primary"
                        loading={addingToCatalog}
                        onClick={async () => {
                            if (!selectedProduct?.id || !selectedCatalog || !selectedVariants[selectedProduct.id]?.length) {
                                message.error('Please select a product, variants, and catalog');
                                return;
                            }

                            try {
                                setAddingToCatalog(true);

                                // Chiamata alla funzione per aggiungere il prodotto al catalogo
                                const result = await addProductToCatalog(
                                    selectedProduct.id,
                                    selectedVariants[selectedProduct.id],
                                    selectedCatalog
                                );

                                setAddResult(result);

                                // Customised message based on whether the product already existed and skipped variants
                                const hasSkippedVariants = result.skippedVariantIds && result.skippedVariantIds.length > 0;

                                if (hasSkippedVariants) {
                                    const skippedCount = result.skippedVariantIds.length;
                                    const addedCount = result.addedVariantIds.length;

                                    if (addedCount > 0) {
                                        message.warning(`${addedCount} variants added successfully. ${skippedCount} variants were already present in the catalogue.`);
                                    } else {
                                        message.error('All selected variants are already present in the catalogue.');
                                        return; // Don't open the success modal
                                    }
                                } else if (result.existingProduct) {
                                    message.success('Variants added to existing product in catalogue');
                                } else {
                                    message.success('Product and variants added to catalogue successfully');
                                }

                                // Call the onFinish function provided by the parent component with result data
                                onFinish({
                                    catalogProductId: result.newProductId,
                                    catalogVariantIds: result.newVariantIds,
                                    originalProductId: result.originalProductId,
                                    originalVariantIds: result.originalVariantIds,
                                    catalogId: result.catalogId
                                });

                                // Deselect all variants for the current product
                                if (selectedProduct && selectedProduct.id) {
                                    handleVariantSelection(selectedProduct.id, [], false);
                                }

                                // Deselect the catalog and license
                                setSelectedCatalog(null);
                                setSelectedLicense(null);

                                // Reset to the first step
                                setCurrentStep(0);
                            } catch (error) {
                                console.error('Error adding product to catalog:', error);
                                message.error('Failed to add product to catalog: ' + error.message);
                            } finally {
                                setAddingToCatalog(false);
                            }
                        }}
                    >
                        {addingToCatalog ? 'Adding...' : 'Confirm'}
                    </Button>
                )}
            </div>

            {/* SKU Upgrade Modal migliorato */}
            <Modal
                title={
                    <div style={{ textAlign: 'center', paddingBottom: '10px' }}>
                        <Title level={4} style={{ margin: 0, color: primaryShades.primary300 }}>
                            Request Subscription Upgrade
                        </Title>
                    </div>
                }
                open={upgradeModal.visible}
                onCancel={() => {
                    setUpgradeModal({ visible: false, subscription: null });
                    setUpgradeMessage('');
                }}
                footer={[
                    <Button key="cancel" onClick={() => {
                        setUpgradeModal({ visible: false, subscription: null });
                        setUpgradeMessage('');
                    }}>
                        Cancel
                    </Button>,
                    <Button
                        key="send"
                        type="primary"
                        loading={sendingUpgradeRequest}
                        onClick={async () => {
                            if (!upgradeModal.subscription || !upgradeMessage.trim()) {
                                message.error('Please enter a message for the upgrade request.');
                                return;
                            }

                            setSendingUpgradeRequest(true);

                            try {
                                // Ottieni le informazioni del profilo utente
                                const userProfile = await getUserProfileForEmail();

                                // Invia la richiesta di upgrade
                                await sendUpgradeRequestEmail(upgradeModal.subscription, userProfile, upgradeMessage);

                                message.success('Upgrade request sent successfully! You will receive a confirmation email shortly.');
                                setUpgradeModal({ visible: false, subscription: null });
                                setUpgradeMessage('');
                            } catch (error) {
                                console.error('Error sending upgrade request:', error);
                                message.error('Failed to send upgrade request. Please try again.');
                            } finally {
                                setSendingUpgradeRequest(false);
                            }
                        }}
                    >
                        {sendingUpgradeRequest ? 'Sending Request...' : 'Send Request'}
                    </Button>
                ]}
                centered
                width={600}
                maskClosable={!sendingUpgradeRequest}
                closable={!sendingUpgradeRequest}
            >
                <div style={{ padding: '20px 0' }}>
                    {/* Informazioni sulla subscription */}
                    <div style={{
                        backgroundColor: primaryShades.primary100,
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: `1px solid ${primaryShades.primary200}`
                    }}>
                        <Title level={5} style={{ margin: '0 0 12px 0', color: primaryShades.primary600 }}>
                            Subscription Details
                        </Title>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            <div>
                                <Text strong>Name: </Text>
                                <Text>{upgradeModal.subscription?.name}</Text>
                            </div>
                            <div>
                                <Text strong>Type: </Text>
                                <Text>{upgradeModal.subscription?.subscriptionType}</Text>
                            </div>
                            <div>
                                <Text strong>Current SKU Limit: </Text>
                                <Text style={{ color: statusTokens.error, fontWeight: 'bold' }}>
                                    {upgradeModal.subscription?.skuLimit === 0 ? 'Unlimited' : upgradeModal.subscription?.skuLimit}
                                </Text>
                            </div>
                            <div>
                                <Text strong>Status: </Text>
                                <Text style={{ color: statusTokens.error, fontWeight: 'bold' }}>Limit Exceeded</Text>
                            </div>
                        </div>
                    </div>

                    {/* Messaggio di spiegazione */}
                    <div style={{
                        backgroundColor: '#fff7e6',
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: `1px solid ${statusTokens.warning}`
                    }}>
                        <Text style={{ color: statusTokens.warning }}>
                            <strong>⚠️ SKU Limit Exceeded</strong>
                        </Text>
                        <br />
                        <Text style={{ color: greyScaleTokens.grey700 }}>
                            You are trying to add more products than your current subscription allows.
                            Please describe your requirements below and our team will review your upgrade request.
                        </Text>
                    </div>

                    {/* Campo per il messaggio */}
                    <div>
                        <Title level={5} style={{ marginBottom: '8px' }}>
                            Describe your upgrade requirements:
                        </Title>
                        <Input.TextArea
                            rows={4}
                            value={upgradeMessage}
                            onChange={(e) => setUpgradeMessage(e.target.value)}
                            placeholder="Please describe:
• How many additional SKUs you need
• Your business requirements
• Timeline for the upgrade
• Any other relevant information..."
                            maxLength={1000}
                            showCount
                            style={{ marginBottom: '12px' }}
                        />
                        <Text style={{ fontSize: '12px', color: greyScaleTokens.grey600 }}>
                            This request will be sent to our support team
                        </Text>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default AddToCatalogStepper;
